import { useNotify, type Identifier } from "ra-core";
import { useEffect, useRef, useState } from "react";

import type { GulaClient } from "../gulaTypes";
import type { ShiftKey } from "../shifts";
import { currentCallSlot } from "../useAgents";
import {
  buildBuckets,
  pickNext,
  roomLeft,
  untouchedCalls,
  type CallMission,
} from "./callPlan";
import { takeCalls } from "./useCallPlan";

/** Refill when fewer than this many untouched calls are waiting… */
export const AUTO_AHEAD = 3;
/** …up to this many, so the next call is always there. */
export const AUTO_FILL_TO = 5;
/** Wait between attempts when nothing could be taken. */
const RETRY_MS = 30_000;
/** Only feed someone who is at the screen: input in the last 15 minutes. */
const ACTIVE_MS = 15 * 60_000;

const STORAGE_KEY = "gula.autopilot";

/** The person's own choice, or null when they never touched the switch. */
const readSetting = (): boolean | null => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value == null ? null : value === "on";
  } catch {
    return null;
  }
};

const writeSetting = (on: boolean) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, on ? "on" : "off");
  } catch {
    // Private windows: the toggle just is not remembered
  }
};

/** How many leads the autopilot should take now (0: leave the list as it is). */
export const autoPilotWant = (untouched: number): number =>
  untouched >= AUTO_AHEAD
    ? 0
    : Math.min(AUTO_FILL_TO - untouched, roomLeft(untouched));

/**
 * Autopilot of Mi jornada: keeps the signed-in person's list topped up with
 * the best next leads of the running shift, for whoever is selling (setters,
 * Mica or Joaquín). It only feeds someone at the screen, and leads left
 * untouched for an hour go back to the pool, so work follows who is active.
 */
export const useAutoPilot = ({
  defaultOn,
  blocked = false,
  clients,
  missions,
  meId,
  onChanged,
}: {
  defaultOn: boolean;
  /** Test or company profiles never get work */
  blocked?: boolean;
  clients: GulaClient[];
  missions: CallMission[];
  meId?: Identifier;
  onChanged: () => void;
}) => {
  const notify = useNotify();
  const [choice, setChoice] = useState(readSetting);
  // The default can change once permissions load (admins start with it off)
  const enabled = !blocked && (choice ?? defaultOn);
  const busy = useRef(false);
  const lastTry = useRef(0);
  const lastInput = useRef(Date.now());
  useEffect(() => {
    const seen = () => {
      lastInput.current = Date.now();
    };
    window.addEventListener("pointerdown", seen);
    window.addEventListener("keydown", seen);
    return () => {
      window.removeEventListener("pointerdown", seen);
      window.removeEventListener("keydown", seen);
    };
  }, []);
  // Re-check now and then: the throttle may have skipped the last change
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const timer = window.setInterval(
      () => setTick((value) => value + 1),
      15_000,
    );
    return () => window.clearInterval(timer);
  }, [enabled]);

  const toggle = (on: boolean) => {
    setChoice(on);
    writeSetting(on);
    lastTry.current = 0;
  };

  useEffect(() => {
    if (!enabled || meId == null || busy.current || !clients.length) return;
    if (Date.now() - lastTry.current < RETRY_MS) return;
    // Nobody at the screen: leave the leads for whoever is working
    if (
      document.visibilityState !== "visible" ||
      Date.now() - lastInput.current > ACTIVE_MS
    )
      return;
    const lastActivity = new Map(
      clients.map((client) => [String(client.id), client.last_activity_at]),
    );
    const want = autoPilotWant(
      untouchedCalls(missions, meId, (id) => lastActivity.get(String(id))),
    );
    if (!want) return;
    const picks = pickNext(
      buildBuckets({
        clients,
        claimedClientIds: new Set(
          missions.map((mission) => String(mission.client_id)),
        ),
        meId,
        shift: (currentCallSlot() as ShiftKey | null) ?? "all",
      }),
      want,
    );
    lastTry.current = Date.now();
    if (!picks.length) return;
    busy.current = true;
    (async () => {
      try {
        for (const pick of picks) {
          await takeCalls(pick.ids, meId, pick.bucket);
        }
      } catch (error) {
        notify(
          error instanceof Error
            ? `Piloto automático: ${error.message}`
            : "Piloto automático: no se han podido coger llamadas",
          { type: "error" },
        );
      } finally {
        busy.current = false;
        onChanged();
      }
    })();
  }, [enabled, clients, missions, meId, onChanged, notify, tick]);

  return { enabled, toggle };
};
